import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './SectionHeader.css';

/**
 * Sub-header that sits between a page-level {@code PageHeader} and a
 * data region (typically a table). Renders an accent icon disk on the
 * left and a stacked title + tagline on the right, giving the data
 * region a concise contextual label without restating the page name.
 *
 * <p>Pattern was first promoted out of the Content Templates page; it
 * standardises the band that previously appeared as ad-hoc one-off
 * markup on every list surface (or, more often, was missing entirely
 * and left the table butted up against the PageHeader with no
 * intermediate context).</p>
 *
 * <p>Optional <code>actions</code> slot is reserved on the far right
 * for future per-section affordances (count chips, secondary buttons,
 * filter pills). The PageHeader still owns primary actions.</p>
 *
 * @param {Object} props
 * @param {import('@fortawesome/fontawesome-svg-core').IconDefinition|React.ReactNode} props.icon - icon shown in the accent disk. A FontAwesome icon definition is wrapped automatically; a React node (e.g. an SVG nav icon) is rendered as-is so page headers can reuse the curated navigation icons.
 * @param {string} props.title - bold heading line (e.g. "Templates List")
 * @param {string|React.ReactNode} props.tagline - one-line description below the title
 * @param {string} [props.tone] - optional accent tone (currently "primary" default)
 * @param {React.ReactNode} [props.actions] - optional right-aligned slot
 * @param {string} [props.className] - extra class for outer container
 */
const SectionHeader = ({ icon, title, tagline, tone = 'primary', actions, className = '' }) => (
    // `app-section-header*` rather than the more obvious `.section-header*` —
    // the unprefixed name is used by page-local styles elsewhere (Cheats,
    // legacy Remote Config form) which set their own borders/padding and
    // would leak onto every instance of this shared component. See the
    // header comment in SectionHeader.css for the full reasoning.
    <div className={`app-section-header app-section-header--${tone} ${className}`.trim()}>
        <span className="app-section-header-icon">
            {React.isValidElement(icon) ? icon : <FontAwesomeIcon icon={icon} />}
        </span>
        <div className="app-section-header-text">
            <h3 className="app-section-header-title">{title}</h3>
            {tagline && <p className="app-section-header-tagline">{tagline}</p>}
        </div>
        {actions && <div className="app-section-header-actions">{actions}</div>}
    </div>
);

export default SectionHeader;
